import {
  Box,
  Stack,
  Typography,
  Avatar,
  Chip,
  alpha,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useHistory } from 'react-router-dom';

import { CommunityActivity } from '@/api/endpoints/community/useGetCommunityActivities';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { MessageCircle01Icon } from '@/assets/icons/MessageCircle01Icon';
import { Settings01Icon } from '@/assets/icons/Settings01Icon';
import { UserPlus01Icon } from '@/assets/icons/UserPlus01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { PMPRoutes } from '@/config/routes';

interface RecentActivityProps {
  activities: CommunityActivity[];
  isLoading?: boolean;
  communityId: number;
}

const getActivityIcon = (type: string) => {
  switch (type) {
  case 'MEMBER_JOINED':
  case 'MEMBER_LEFT':
  case 'MEMBER_VERIFIED':
  case 'MEMBER_ROLE_CHANGED':
    return <UserPlus01Icon width={16} height={16} />;
  case 'GROUP_CREATED':
  case 'GROUP_JOINED':
  case 'GROUP_LEFT':
  case 'GROUP_COMPLETED':
  case 'GROUP_CLOSED':
    return <MessageCircle01Icon width={16} height={16} />;
  case 'SETTINGS_UPDATED':
  case 'RULES_UPDATED':
  case 'ADMIN_ACTION':
    return <Settings01Icon width={16} height={16} />;
  default:
    return <CalendarIcon width={16} height={16} />;
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
  case 'MEMBER_JOINED':
  case 'MEMBER_VERIFIED':
  case 'GROUP_CREATED':
  case 'GROUP_COMPLETED':
    return '#4caf50'; // Green for positive actions
  case 'MEMBER_LEFT':
  case 'GROUP_LEFT':
  case 'GROUP_CLOSED':
    return '#ff9800'; // Orange for neutral/departure actions
  case 'SETTINGS_UPDATED':
  case 'RULES_UPDATED':
  case 'ADMIN_ACTION':
    return '#9D1500'; // Primary red for admin actions
  case 'GROUP_JOINED':
    return '#2196f3'; // Blue for participation
  default:
    return '#757575'; // Gray for system actions
  }
};

const getActivityType = (type: string) => {
  switch (type) {
  case 'MEMBER_JOINED':
    return 'Member';
  case 'MEMBER_LEFT':
    return 'Member';
  case 'MEMBER_VERIFIED':
    return 'Member';
  case 'MEMBER_ROLE_CHANGED':
    return 'Member';
  case 'GROUP_CREATED':
    return 'Group';
  case 'GROUP_JOINED':
    return 'Group';
  case 'GROUP_LEFT':
    return 'Group';
  case 'GROUP_COMPLETED':
    return 'Group';
  case 'GROUP_CLOSED':
    return 'Group';
  case 'SETTINGS_UPDATED':
    return 'Settings';
  case 'RULES_UPDATED':
    return 'Rules';
  case 'ADMIN_ACTION':
    return 'Admin';
  default:
    return 'System';
  }
};

const RecentActivity: React.FC<RecentActivityProps> = ({ activities, isLoading, communityId }) => {
  const history = useHistory();

  const handleActivityClick = (activity: CommunityActivity) => {
    // Check if it's a group activity with target_id
    if (
      (activity.type === 'GROUP_CREATED' ||
        activity.type === 'GROUP_JOINED' ||
        activity.type === 'GROUP_LEFT' ||
        activity.type === 'GROUP_CLOSED' ||
        activity.type === 'GROUP_COMPLETED') &&
      activity.target_id
    ) {
      // Navigate to group negotiation detail page
      const groupRoute = PMPRoutes.groupNegotiationView
        .replace(':communityId', communityId.toString())
        .replace(':negotiationId', activity.target_id);
      history.push(groupRoute);
    }
  };

  const isClickable = (activity: CommunityActivity) => (
    (activity.type === 'GROUP_CREATED' ||
        activity.type === 'GROUP_JOINED' ||
        activity.type === 'GROUP_LEFT' ||
        activity.type === 'GROUP_CLOSED' ||
        activity.type === 'GROUP_COMPLETED') &&
      activity.target_id
  );
  if (isLoading) {
    return (
      <Stack spacing={2}>
        {Array.from({ length: 3 }, (_, index) => (
          <Box
            key={`skeleton-${index}`}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'secondary.50',
              border: '1px solid',
              borderColor: 'secondary.100',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'secondary.200',
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    height: 16,
                    bgcolor: 'secondary.200',
                    borderRadius: 1,
                    mb: 1,
                    width: '80%',
                  }}
                />
                <Box
                  sx={{
                    height: 12,
                    bgcolor: 'secondary.200',
                    borderRadius: 1,
                    width: '40%',
                  }}
                />
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          borderRadius: 2,
          bgcolor: 'secondary.50',
        }}
      >
        <Users02Icon
          width={32}
          height={32}
          style={{ color: '#9e9e9e', marginBottom: '8px' }}
        />
        <Typography variant="body1" color="secondary.600">
          No recent activity to display.
        </Typography>
        <Typography variant="body2" color="secondary.500" sx={{ mt: 0.5 }}>
          Community activities will appear here as members interact.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {activities.map((activity) => {
        const activityColor = getActivityColor(activity.type);
        const activityType = getActivityType(activity.type);
        
        return (
          <Box
            key={activity.id}
            onClick={() => handleActivityClick(activity)}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'secondary.50',
              border: '1px solid',
              borderColor: 'secondary.100',
              transition: 'all 0.2s ease-in-out',
              cursor: isClickable(activity) ? 'pointer' : 'default',
              '&:hover': {
                borderColor: alpha(activityColor, 0.3),
                bgcolor: alpha(activityColor, 0.02),
                ...(isClickable(activity) && {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(activityColor, 0.15)}`,
                }),
              },
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(activityColor, 0.1),
                  color: activityColor,
                }}
              >
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: 'secondary.900',
                      lineHeight: 1.4,
                    }}
                  >
                    {activity.action}
                  </Typography>
                  <Chip
                    label={activityType}
                    size="small"
                    sx={{
                      bgcolor: alpha(activityColor, 0.1),
                      color: activityColor,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: 20,
                      ml: 1,
                      flexShrink: 0,
                    }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="secondary.600"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default RecentActivity;